// Envio do lead para o funil do ERP (crm-biancode).
// Guia: docs/integracao-form-leads.md no repo crm-biancode.
//
// É um POST JSON simples: sem header de autenticação, sem SDK, CORS liberado.
//
// Não vai mais segredo nenhum daqui. Ele ficava no corpo do JSON e, como este
// código roda no navegador, ia junto no bundle público: qualquer visitante
// lia o main.js e tinha a credencial. Credencial que viaja no navegador não é
// credencial. Quem decide se o envio entra é o servidor, pela origem da
// requisição, pelo campo-armadilha e pelo limite de taxa.

import { dadosDaEmpresa, exigeQualificacaoEmpresa } from '../components/ContactPage/companyQualification';

const CRM_LEAD_URL =
  "https://kong-production-c00c.up.railway.app/functions/v1/submit-lead";

/**
 * Envia o lead completo para o ERP.
 * Nunca lança: o visitante não pode ver erro por causa do CRM. O lead também
 * fica no Firebase, então falha aqui não perde o contato.
 *
 * @returns {Promise<{success: boolean, leadId?: string, repetido?: boolean, error?: string}>}
 */
export const submitLeadToCrm = async (payload) => {
  try {
    const response = await fetch(CRM_LEAD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      console.error(`[CRM] Falha ${response.status}:`, body.error || body);
      return { success: false, error: body.error };
    }

    // repetido = a pessoa já tinha lead aberto e isto virou anotação no card.
    // É sucesso, não erro.
    console.log(
      `[CRM] Lead registrado (id: ${body.lead_id}, repetido: ${body.repetido})`
    );
    return { success: true, leadId: body.lead_id, repetido: Boolean(body.repetido) };
  } catch (error) {
    console.error("[CRM] Erro de rede:", error);
    return { success: false, error: "rede" };
  }
};

/**
 * Traduz o formulário da página de contato para os campos do guia.
 * Campo fora da tabela do guia é guardado do mesmo jeito pelo servidor, então
 * mandamos o que temos de contexto (score, tracking, origem) sem mapear.
 */
export const buildCrmPayload = ({ form, phone, orcamentoErp, score, tier, trackingId, lpSource, lpVariant, utmParams, raiox, metaEventId, atribuicao, consent, isca, plataforma }) => {
  const payload = {
    nome: form.name.trim(),
    email: form.email.trim(),
    telefone: phone,
    empresa: form.company.trim(),
    cargo: form.role.trim(),
    servico: form.productType,
    // O público-alvo vira `setor`, que é coluna de verdade em leads e aparece
    // como etiqueta no card do quadro. Campo extra ficaria invisível para quem
    // só olha o quadro, e "B2B" na etiqueta já muda a conversa do comercial.
    setor: form.audience,
    // Agora é orçamento DO PROJETO, que é o que esta coluna sempre quis dizer.
    // Faturamento real fica no bloco qualificacao_empresa, nunca nesta coluna:
    // receita da empresa não é o valor do projeto que ela quer contratar.
    orcamento: orcamentoErp,
    pagina: window.location.pathname,

    // Extras: o guia garante que campo fora da tabela é preservado.
    orcamento_faixa: form.budget,
    fase_produto: form.stage,
    tipo_produto: form.productType,
    publico_alvo: form.audience,
    lead_score: score,
    lead_tier: tier,
    tracking_id: trackingId,
    lp_source: lpSource,
    lp_variant: lpVariant,
    // Guardado para a API de Conversões: o servidor repete este id e a Meta
    // reconhece a conversão que o navegador já mandou.
    meta_event_id: metaEventId,
  };

  // Bloco próprio para não sobrescrever a receita projetada do Raio-X,
  // o orçamento do projeto ou um diagnóstico anterior da operação.
  if (exigeQualificacaoEmpresa(window.location.pathname)) {
    const empresa = dadosDaEmpresa(form);
    if (empresa) payload.qualificacao_empresa = empresa;
  }

  // O servidor só avisa Meta e Google da conversão com consentimento de
  // publicidade. Sem este campo ele fica inerte, que é o lado certo para errar.
  if (consent) payload.consent = consent;

  // De onde veio o último clique, na mesma classificação que a landing usa.
  // É por este campo que o painel de tráfego agrupa.
  if (plataforma) payload.plataforma = plataforma;

  // Campo-armadilha. Só viaja quando tem conteúdo, que é quando o servidor
  // precisa dele: mandar vazio em todo lead só sujaria o payload guardado.
  if (isca && String(isca).trim()) payload.website = isca;

  // Primeira visita e último clique, como o atribuicao.js gravou no cookie de
  // .biancode.com.br. É o que liga o lead à campanha quando a pessoa fez o
  // Raio-X no infoprodutor e só veio converter aqui.
  if (atribuicao?.primeiro) payload.attr_first = atribuicao.primeiro;
  if (atribuicao?.ultimo) payload.attr_last = atribuicao.ultimo;

  // O cenário que a pessoa escreveu é o que o comercial lê antes de ligar.
  if (form.scenario && form.scenario.trim()) payload.mensagem = form.scenario.trim();

  // Números do Raio-X do Vale: o comercial precisa ver no card por que o lead veio.
  if (raiox) {
    payload.raiox_base = raiox.base;
    payload.raiox_ultimo_lancamento = raiox.ultimo;
    payload.raiox_lancamentos_ano = raiox.lanc;
    if (raiox.mensalidade) payload.raiox_mensalidade = raiox.mensalidade;
    payload.raiox_receita_mes = raiox.receita;
    payload.raiox_receita_vale = raiox.vale;
    payload.raiox_prioridade = raiox.prioridade ? "sim" : "nao";
    payload.raiox_nicho = raiox.n;
  }

  if (utmParams) {
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((key) => {
      if (utmParams[key]) payload[key] = utmParams[key];
    });
  }

  return payload;
};
